import { FunctionalityEnum, SearchResultTypeEnum } from '@elearning-platform/api-types/enums';
import { SearchElementInterface, SearchResultInterface } from '@elearning-platform/api-types/interfaces';
import {
  Breakpoint,
  Color,
  EmptyPlaceholder,
  Icon,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  IconName,
  SimpleMarkdown,
  useNotifications,
  UseNotifications,
  UseState,
} from '@elearning-platform/ui';
import { useMediaQuery } from '@mui/material';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { NavigateFunction, useNavigate } from 'react-router';
import { DebouncedState, useDebounce } from 'use-debounce';

import { downloadFile, searchNewsResults, searchResults } from '../../api/requests';
import { QueryKey, RouteNameEnum, SearchPopupPhase, ViewImage } from '../../enums';
import { useAuth, useBodyScrollLock, useRedirectTo, useVolatileSettings } from '../../hooks';
import { UseAuth, UseRedirectTo, UseVolatileSettings } from '../../interfaces';
import { ItemType } from '../thumbnail/thumbnail.constants';

import {
  Container,
  NewsHeader,
  ResultsContainer,
  SearchBarContainer,
  SearchBarInput,
  SearchBarResults,
  SearchBarWrapper,
  StyledThumbnail,
  ThumbnailCategory,
  ThumbnailDescription,
  ThumbnailDetails,
  ThumbnailDetailsContent,
  ThumbnailRow,
  ThumbnailTitle,
} from './searchPopup.styled';

export const SearchPopup: React.FC = (): JSX.Element => {
  const { t }: TransProps<never> = useTranslation();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const navigate: NavigateFunction = useNavigate();
  const isTablet: boolean = useMediaQuery(Breakpoint.Tablet);
  const { hasUserRequiredFunctionalities }: UseAuth = useAuth();
  const { handleSearchPopupClose, volatileSettings }: UseVolatileSettings = useVolatileSettings();
  const [searchValue, setSearchValue] = useState(volatileSettings.searchPopupSettings?.initialQuery ?? '');
  const [debouncedSearch]: [string, DebouncedState<(value: string) => void>] = useDebounce(searchValue, 500);
  const [isDownloadLoading, setIsDownloadLoading]: UseState<Record<string, boolean>> = useState<Record<string, boolean>>({});
  const { showPopup, hidePopup }: UseNotifications = useNotifications();
  const hasApiNewsAccess: boolean = useMemo(
    (): boolean => hasUserRequiredFunctionalities([FunctionalityEnum.ApiNews]),
    [hasUserRequiredFunctionalities]
  );
  useBodyScrollLock(volatileSettings.searchPopupSettings?.phase !== SearchPopupPhase.Closed);

  const { data, isFetching }: UseQueryResult<SearchResultInterface[]> = useQuery({
    queryKey: [QueryKey.SearchResults, debouncedSearch],
    queryFn: (): Promise<SearchResultInterface[]> => searchResults(debouncedSearch),
    enabled: debouncedSearch.length > 2,
  });

  const { data: newsData, isFetching: isNewsFetching }: UseQueryResult<SearchElementInterface[]> = useQuery({
    queryKey: [QueryKey.SearchNewsResults, debouncedSearch, hasApiNewsAccess],
    queryFn: (): Promise<SearchElementInterface[]> => searchNewsResults(debouncedSearch),
    enabled: !!(hasApiNewsAccess && debouncedSearch.length > 2),
  });

  useEffect((): void | (() => void) => {
    if (volatileSettings.searchPopupSettings?.phase === SearchPopupPhase.Closed) {
      return;
    }

    const handleKeyDown: (event: KeyboardEvent) => void = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        handleSearchPopupClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return (): void => document.removeEventListener('keydown', handleKeyDown);
  }, [handleSearchPopupClose, volatileSettings.searchPopupSettings?.phase]);

  const handleDownload: (id: number) => Promise<void | AxiosResponse<void>> = (id: number): Promise<void | AxiosResponse<void>> => {
    setIsDownloadLoading((prev: Record<string, boolean>): Record<string, boolean> => ({ ...prev, [id]: true }));

    return downloadFile(id)
      .catch((error: Error): void => {
        showPopup({
          title: t('competence.app.global.errorOccurred'),
          description: error.message || t('competence.app.global.defaultErrorDescription'),
          acceptButtonData: {
            label: t('competence.app.global.close'),
            onClick: (): void => hidePopup(),
          },
        });
      })
      .finally((): void => {
        setIsDownloadLoading((prev: Record<string, boolean>): Record<string, boolean> => ({ ...prev, [id]: false }));
      });
  };

  const handleSearchResultAction: (result: SearchResultInterface) => Promise<void>
  = async (result: SearchResultInterface): Promise<void> => {
    if (result.type === SearchResultTypeEnum.Download) {
      await handleDownload(result.id);
    } else if (result.type !== SearchResultTypeEnum.Files && result.type !== SearchResultTypeEnum.ContentGroup) {
      if (result.url) {
        navigate(result.url);
      } else {
        redirect({ route: RouteNameEnum.Dashboard });
      }
      handleSearchPopupClose();
    }
  };

  const getCategoryLabel: (type: SearchResultTypeEnum) => string = (type: SearchResultTypeEnum): string => {
    switch (type) {
      case SearchResultTypeEnum.Content:
        return t('competence.app.searchPopup.elearning');
      case SearchResultTypeEnum.ContentGroup:
        return t('competence.app.searchPopup.videoGroup');
      case SearchResultTypeEnum.Event:
        return t('competence.app.searchPopup.training');
      case SearchResultTypeEnum.Files:
      case SearchResultTypeEnum.Download:
        return t('competence.app.searchPopup.filesToDownload');
      default:
        // eslint-disable-next-line no-case-declarations
        const _: never = type;

        return type;
    }
  };

  const handleNewsRedirect: (id: number) => void = (id: number): void => {
    redirect({ route: RouteNameEnum.NewsEntry, params: { id } });
    handleSearchPopupClose();
  };

  return (
    <Container $visible={volatileSettings.searchPopupSettings?.phase === SearchPopupPhase.Open}>
      <SearchBarWrapper>
        <SearchBarContainer>
          <Icon color={Color.Brand300} name={IconName.Search} />
          <SearchBarInput
            autoFocus
            onChange={(event: ChangeEvent<HTMLInputElement>): void => setSearchValue(event.target.value)}
            placeholder={t('competence.app.searchPopup.searchPlatform')}
            value={searchValue}
          />
          {(debouncedSearch.length > 2 && !!isTablet) && (
            <SearchBarResults>
              {t('competence.app.searchPopup.itemsFound', { itemsCount: (data?.length ?? 0) + (newsData?.length ?? 0) })}
            </SearchBarResults>
          )}
        </SearchBarContainer>
        <IconButton
          icon={IconName.Clear}
          onClick={handleSearchPopupClose}
          size={IconButtonSize.Small}
          variant={IconButtonVariant.PrimaryGray}
        />
      </SearchBarWrapper>
      {(debouncedSearch.length > 2 && !isTablet) && (
        <SearchBarResults>
          {t('competence.app.searchPopup.itemsFound', { itemsCount: (data?.length ?? 0) + (newsData?.length ?? 0) })}
        </SearchBarResults>
      )}
      <ResultsContainer>
        {(data?.length || newsData?.length) ? (
          <>
            {data?.map((result: SearchResultInterface): JSX.Element => (
              <ThumbnailRow key={result.id}>
                <StyledThumbnail
                  action={(): Promise<void> => handleSearchResultAction(result)}
                  controlledHeight
                  hideTitle
                  image={result.image}
                  isDownloadLoading={isDownloadLoading[result.id]}
                  isSearchView
                  items={result.dto?.items}
                  title={result.name}
                  type={(result.type === SearchResultTypeEnum.Download || result.type === SearchResultTypeEnum.Files)
                    ? ItemType.Files
                    : result.dto?.items?.length
                      ? ItemType.Contents
                      : ItemType.Video
                  }
                  withFavouriteActionButton={false}
                />
                <ThumbnailDetails>
                  <ThumbnailDetailsContent>
                    <ThumbnailTitle
                      $disabled={result.type === SearchResultTypeEnum.Files || result.type === SearchResultTypeEnum.ContentGroup}
                      onClick={(): Promise<void> => handleSearchResultAction(result)}
                    >
                      {result.name}
                    </ThumbnailTitle>
                    <ThumbnailCategory>
                      {getCategoryLabel(result.type)}
                    </ThumbnailCategory>
                    <ThumbnailDescription>
                      {result.description}
                    </ThumbnailDescription>
                  </ThumbnailDetailsContent>
                </ThumbnailDetails>
              </ThumbnailRow>
            ))}
            {newsData?.map((result: SearchElementInterface): JSX.Element => (
              <ThumbnailRow key={result.id}>
                <StyledThumbnail
                  action={(): void => handleNewsRedirect(result.id)}
                  image={result.image ?? ViewImage.SearchPopupNewsDefaultImage}
                  isSearchView
                  withFavouriteActionButton={false}
                />
                <ThumbnailDetails>
                  <ThumbnailDetailsContent>
                    <ThumbnailTitle onClick={(): void => handleNewsRedirect(result.id)}>
                      <NewsHeader
                        text={t('competence.app.searchPopup.newsHeader', {
                          newsName: result.name,
                          newsAuthorName: result.dto.publisher.firstName,
                          newsAuthorSurname: result.dto.publisher.lastName,
                        })}
                      />
                    </ThumbnailTitle>
                    <ThumbnailCategory>
                      {t('competence.app.searchPopup.news')}
                    </ThumbnailCategory>
                    <ThumbnailDescription>
                      <SimpleMarkdown text={result.description ?? ''} />
                    </ThumbnailDescription>
                  </ThumbnailDetailsContent>
                </ThumbnailDetails>
              </ThumbnailRow>
            ))}
          </>
        ) : (
          <EmptyPlaceholder
            colorsData={{ bgColor: Color.White }}
            isLoading={isFetching || isNewsFetching}
            text={(isNewsFetching || isFetching)
              ? t('competence.app.searchPopup.loading')
              : debouncedSearch.length > 2
                ? t('competence.app.searchPopup.noResults')
                : t('competence.app.searchPopup.minCharactersHint')
            }
          />
        )}
      </ResultsContainer>
    </Container>
  );
};
